/**
 * The Shade — live "you're being stalked" pop-up.
 *
 * A global watcher (mountShadeWatcher, called once from main.ts) polls the
 * player's dark-raid status on a light interval. The instant the Shade fixes on
 * them, a blocking modal appears — wherever they are in the app — forcing them
 * to acknowledge it and routing them to the Hall of Combat (the Attacks page),
 * where the existing banner lets them confront it now or leave it for the
 * window. The modal is purely a herald: it grants nothing and resolves nothing.
 */
import { store } from '../state/store.js';
import { api } from '../api/client.js';
import { navigate } from '../router.js';

const POLL_MS = 45_000;

let mounted = false;
let pollHandle: number | null = null;
/** The windowEndsAt of the mark we last popped, so we don't re-show the same
 *  hunt every poll. Reset when the mark clears, so the NEXT hunt pops afresh. */
let lastShownWindow: string | null = null;

export function mountShadeWatcher(): void {
  if (mounted) return;
  mounted = true;

  const poll = async (): Promise<void> => {
    // Only while signed in with a character.
    if (!store.get().character) return;
    try {
      const s = await api.darkRaidStatus();
      if (s.marked && s.windowEndsAt) {
        if (s.windowEndsAt !== lastShownWindow) {
          lastShownWindow = s.windowEndsAt;
          showShadeModal(s.windowEndsAt);
        }
      } else {
        lastShownWindow = null;
      }
    } catch {
      /* best-effort — the watcher must never throw */
    }
  };

  void poll();
  pollHandle = window.setInterval(() => void poll(), POLL_MS);
}

export function stopShadeWatcher(): void {
  if (pollHandle !== null) {
    window.clearInterval(pollHandle);
    pollHandle = null;
  }
  mounted = false;
}

function fmtRemaining(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return 'now';
  const sec = Math.ceil(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function showShadeModal(windowEndsAt: string): void {
  // Idempotent — never stack two.
  document.getElementById('shade-modal')?.remove();
  document.getElementById('shade-overlay')?.remove();

  if (!document.getElementById('shade-styles')) {
    const style = document.createElement('style');
    style.id = 'shade-styles';
    style.textContent = SHADE_STYLES;
    document.head.append(style);
  }

  const overlay = document.createElement('div');
  overlay.id = 'shade-overlay';
  overlay.className = 'shade-overlay';
  document.body.append(overlay);

  const modal = document.createElement('div');
  modal.id = 'shade-modal';
  modal.className = 'shade-modal';
  modal.innerHTML = `
    <img class="shade-portrait" src="/img/shade/shade.png" alt=""
      onerror="this.replaceWith(Object.assign(document.createElement('div'),{className:'shade-glyph',textContent:'🕯'}))" />
    <h2 class="shade-title">The Shade Has Found You</h2>
    <p class="shade-body">
      You climbed too high, and something beneath the realm opened its eyes. A Shade
      has your scent now — it does not sleep, it does not tire, and it does not forget.
      Your breaths have been numbered, and found few. You cannot hide from the hell
      that lives in the dark, and it has no use for your coin or your screams. Stand
      and face it in the Hall of Combat while the choice is still yours — turn your
      back, and it will cut you down and leave you cold and broken in the long dark,
      before the realm has even forgotten your name.
    </p>
    <p class="shade-cd">It closes on you in <strong id="shade-cd">${fmtRemaining(windowEndsAt)}</strong>.</p>
    <div class="shade-actions">
      <button class="btn" id="shade-go">Face the Shade</button>
      <button class="btn ghost" id="shade-later">Cower — not yet</button>
    </div>
  `;
  document.body.append(modal);

  const cdEl = modal.querySelector<HTMLElement>('#shade-cd');
  const cdTimer = window.setInterval(() => {
    if (!cdEl || !document.body.contains(modal)) {
      window.clearInterval(cdTimer);
      return;
    }
    cdEl.textContent = fmtRemaining(windowEndsAt);
  }, 1000);

  const close = (): void => {
    window.clearInterval(cdTimer);
    overlay.remove();
    modal.remove();
  };
  // No click-outside dismiss: the herald demands a choice.
  modal.querySelector<HTMLButtonElement>('#shade-go')!.addEventListener('click', () => {
    close();
    navigate('/attacks');
  });
  modal.querySelector<HTMLButtonElement>('#shade-later')!.addEventListener('click', close);
}

const SHADE_STYLES = `
  .shade-overlay {
    position: fixed; inset: 0; z-index: 400;
    background: rgba(6, 3, 10, 0.9);
    backdrop-filter: blur(3px);
  }
  .shade-modal {
    position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: min(680px, 95vw); max-height: 95vh; overflow-y: auto; z-index: 401;
    background: var(--c-parchment); background-image: var(--parchment-texture);
    border: 2px solid #5b2a7a;
    box-shadow: 0 0 0 1px #7d3aa6, 0 0 60px rgba(125, 58, 166, 0.45), 0 24px 70px rgba(0, 0, 0, 0.8);
    border-radius: var(--radius-lg);
    padding: var(--s-6); text-align: center;
  }
  .shade-portrait {
    display: block; margin: 0 auto var(--s-4);
    width: auto; max-width: 100%; max-height: 56vh; object-fit: contain;
    border: 1px solid #5b2a7a; border-radius: var(--radius);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.7);
  }
  .shade-glyph { font-size: 4rem; line-height: 1; margin-bottom: var(--s-3); }
  .shade-title {
    font-family: var(--font-heading); text-transform: uppercase; letter-spacing: 0.08em;
    color: #6d2f93; margin: 0 0 var(--s-3) 0; font-size: 2rem;
  }
  .shade-body { color: var(--c-ink); line-height: 1.6; margin: 0 auto var(--s-3); max-width: 640px; font-size: 1.05rem; }
  .shade-cd { color: var(--c-oxblood); font-family: var(--font-numeric); margin: 0 0 var(--s-4) 0; font-size: 1.05rem; }
  .shade-actions { display: flex; gap: var(--s-3); justify-content: center; flex-wrap: wrap; }
`;
